const VERTEX_SHADER = `
attribute vec2 aPosition;
varying vec2 vUv;

void main() {
  vUv = aPosition * 0.5 + 0.5;
  gl_Position = vec4(aPosition, 0.0, 1.0);
}
`

const INIT_DYE_SHADER = `
precision PRECISION float;

varying vec2 vUv;

uniform vec2 uResolution;
uniform float uSeed;

float hash(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32 + uSeed);
  return fract(p.x * p.y);
}

vec2 hash2(vec2 p) {
  return vec2(hash(p), hash(p + 19.17));
}

float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);

  float a = hash(i);
  float b = hash(i + vec2(1.0, 0.0));
  float c = hash(i + vec2(0.0, 1.0));
  float d = hash(i + vec2(1.0, 1.0));

  return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
}

float fbm(vec2 p) {
  float value = 0.0;
  float amplitude = 0.5;

  for (int i = 0; i < 5; i++) {
    value += noise(p) * amplitude;
    p = mat2(1.57, 1.12, -1.12, 1.57) * p + 4.1;
    amplitude *= 0.52;
  }

  return value;
}

vec2 rotate(vec2 p, float angle) {
  float s = sin(angle);
  float c = cos(angle);
  return mat2(c, -s, s, c) * p;
}

float band(float value, float center, float width) {
  float d = abs(fract(value) - center);
  d = min(d, 1.0 - d);
  return 1.0 - smoothstep(width, width + 0.012, d);
}

vec2 marbleDrop(vec2 p, vec2 center, float radius, float strength) {
  vec2 delta = p - center;
  float dist = length(delta) + 0.0007;
  float influence = exp(-(dist * dist) / max(radius * radius, 0.0001));
  float expanded = sqrt(dist * dist + radius * radius * strength * influence);
  return center + delta * (expanded / dist);
}

vec2 comb(vec2 p, float angle, float frequency, float amplitude, float phase) {
  vec2 q = rotate(p, angle);
  q.x += sin(q.y * frequency + phase) * amplitude;
  q.x += sin(q.y * frequency * 0.43 - phase * 0.7) * amplitude * 0.55;
  return rotate(q, -angle);
}

float cellInk(vec2 p) {
  vec2 grid = p * 4.25 + vec2(2.1, -0.7);
  vec2 cell = floor(grid);
  vec2 local = fract(grid);
  float result = 0.0;

  for (int y = -1; y <= 1; y++) {
    for (int x = -1; x <= 1; x++) {
      vec2 offset = vec2(float(x), float(y));
      vec2 id = cell + offset;
      vec2 point = hash2(id) * 0.78 + 0.11;
      float radius = mix(0.09, 0.34, hash(id + 6.3));
      float d = length(offset + point - local);
      float spot = 1.0 - smoothstep(radius, radius + 0.018, d);
      result = max(result, spot);
    }
  }

  return result;
}

float dropRing(vec2 p, vec2 center, float radius, float width) {
  return 1.0 - smoothstep(width, width + 0.012, abs(length(p - center) - radius));
}

void main() {
  gl_FragColor = vec4(vec3(0.0), 1.0);
}
`

const INIT_VELOCITY_SHADER = `
precision PRECISION float;

varying vec2 vUv;

uniform float uSeed;

float hash(vec2 p) {
  p = fract(p * vec2(127.1, 311.7));
  p += dot(p, p + 17.13 + uSeed);
  return fract(p.x * p.y);
}

float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  float a = hash(i);
  float b = hash(i + vec2(1.0, 0.0));
  float c = hash(i + vec2(0.0, 1.0));
  float d = hash(i + vec2(1.0, 1.0));
  return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
}

float fbm(vec2 p) {
  float value = 0.0;
  float amplitude = 0.5;

  for (int i = 0; i < 4; i++) {
    value += noise(p) * amplitude;
    p = mat2(1.6, 1.2, -1.2, 1.6) * p + 3.8;
    amplitude *= 0.5;
  }

  return value;
}

vec2 encodeVelocity(vec2 velocity) {
  return clamp(velocity * 0.5 + 0.5, 0.0, 1.0);
}

void main() {
  gl_FragColor = vec4(encodeVelocity(vec2(0.0)), 0.0, 1.0);
}
`

const UPDATE_VELOCITY_SHADER = `
precision PRECISION float;

varying vec2 vUv;

uniform sampler2D uVelocity;
uniform vec2 uTexelSize;
uniform vec2 uAspect;
uniform vec2 uPointer;
uniform vec2 uPointerPrevious;
uniform vec2 uPointerVelocity;
uniform float uPointerActive;
uniform float uMotionEnergy;
uniform float uTime;
uniform float uDt;
uniform float uSeed;

float hash(vec2 p) {
  p = fract(p * vec2(127.1, 311.7));
  p += dot(p, p + 17.13 + uSeed);
  return fract(p.x * p.y);
}

float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  float a = hash(i);
  float b = hash(i + vec2(1.0, 0.0));
  float c = hash(i + vec2(0.0, 1.0));
  float d = hash(i + vec2(1.0, 1.0));
  return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
}

float fbm(vec2 p) {
  float value = 0.0;
  float amplitude = 0.5;

  for (int i = 0; i < 4; i++) {
    value += noise(p) * amplitude;
    p = mat2(1.62, 1.09, -1.09, 1.62) * p + 8.7;
    amplitude *= 0.52;
  }

  return value;
}

vec2 decodeVelocity(vec4 value) {
  return value.xy * 2.0 - 1.0;
}

vec2 encodeVelocity(vec2 velocity) {
  return clamp(velocity * 0.5 + 0.5, 0.0, 1.0);
}

float segmentDistance(vec2 point, vec2 start, vec2 end) {
  vec2 segment = (end - start) * uAspect;
  vec2 offset = (point - start) * uAspect;
  float h = clamp(dot(offset, segment) / max(dot(segment, segment), 0.00001), 0.0, 1.0);
  return length(offset - segment * h);
}

void main() {
  vec2 previousVelocity = decodeVelocity(texture2D(uVelocity, vUv));
  vec2 advectedUv = clamp(vUv - previousVelocity * uDt * 0.42, 0.001, 0.999);
  vec2 velocity = decodeVelocity(texture2D(uVelocity, advectedUv));
  vec2 neighborVelocity = vec2(0.0);
  neighborVelocity += decodeVelocity(texture2D(uVelocity, advectedUv + vec2(uTexelSize.x, 0.0)));
  neighborVelocity += decodeVelocity(texture2D(uVelocity, advectedUv - vec2(uTexelSize.x, 0.0)));
  neighborVelocity += decodeVelocity(texture2D(uVelocity, advectedUv + vec2(0.0, uTexelSize.y)));
  neighborVelocity += decodeVelocity(texture2D(uVelocity, advectedUv - vec2(0.0, uTexelSize.y)));
  neighborVelocity *= 0.25;
  velocity = mix(velocity, neighborVelocity, 0.16);

  vec2 noiseUv = vUv * uAspect;
  vec2 pondA = noiseUv * 1.45 + vec2(sin(uTime * 0.023), cos(uTime * 0.019)) * 0.34;
  vec2 pondB = noiseUv * 3.1 + vec2(cos(uTime * 0.017), sin(uTime * 0.029)) * 0.28 + 8.7;
  float offsetA = 0.018;
  float offsetB = 0.011;
  float l = fbm(pondA - vec2(offsetA, 0.0));
  float r = fbm(pondA + vec2(offsetA, 0.0));
  float b = fbm(pondA - vec2(0.0, offsetA));
  float t = fbm(pondA + vec2(0.0, offsetA));
  vec2 ambientCurl = vec2(t - b, l - r);
  l = fbm(pondB - vec2(offsetB, 0.0));
  r = fbm(pondB + vec2(offsetB, 0.0));
  b = fbm(pondB - vec2(0.0, offsetB));
  t = fbm(pondB + vec2(0.0, offsetB));
  vec2 surfaceCurl = vec2(t - b, l - r);
  velocity += (ambientCurl * 0.72 + surfaceCurl * 0.34) * ((0.018 + 0.16 * uMotionEnergy) * uDt);

  float cursorDistance = segmentDistance(vUv, uPointerPrevious, uPointer);
  float pointerSpeed = clamp(length(uPointerVelocity * uAspect) * 34.0, 0.0, 1.0);
  float radius = mix(0.007, 0.018, pointerSpeed);
  float influence = exp(-(cursorDistance * cursorDistance) / max(radius * radius, 0.0001)) * uPointerActive;
  vec2 offset = (vUv - uPointer) * uAspect;
  vec2 tangent = normalize(vec2(-offset.y, offset.x) + 0.0001);
  vec2 drag = uPointerVelocity * vec2(uAspect.x, 1.0) * 6.2;
  velocity += drag * influence;
  velocity += tangent * influence * (0.006 + pointerSpeed * 0.028);

  float edgeFade = smoothstep(0.0, 0.08, vUv.x) *
    smoothstep(0.0, 0.08, vUv.y) *
    smoothstep(0.0, 0.08, 1.0 - vUv.x) *
    smoothstep(0.0, 0.08, 1.0 - vUv.y);
  velocity *= mix(0.72, 1.0, edgeFade);
  velocity *= mix(0.925, 0.976, uMotionEnergy);
  velocity = clamp(velocity, vec2(-0.42), vec2(0.42));

  gl_FragColor = vec4(encodeVelocity(velocity), 0.0, 1.0);
}
`

const ADVECT_DYE_SHADER = `
precision PRECISION float;

varying vec2 vUv;

uniform sampler2D uDye;
uniform sampler2D uVelocity;
uniform sampler2D uStillness;
uniform vec2 uTexelSize;
uniform float uDt;
uniform float uIdleTime;

vec2 decodeVelocity(vec4 value) {
  return value.xy * 2.0 - 1.0;
}

void main() {
  vec2 velocity = decodeVelocity(texture2D(uVelocity, vUv));
  float edgeFade = smoothstep(0.0, 0.055, vUv.x) *
    smoothstep(0.0, 0.055, vUv.y) *
    smoothstep(0.0, 0.055, 1.0 - vUv.x) *
    smoothstep(0.0, 0.055, 1.0 - vUv.y);
  vec2 coord = clamp(vUv - velocity * uDt * 0.48 * edgeFade, 0.001, 0.999);
  vec3 color = texture2D(uDye, coord).rgb;

  vec3 neighborhood = vec3(0.0);
  neighborhood += texture2D(uDye, coord + vec2(uTexelSize.x, 0.0)).rgb;
  neighborhood += texture2D(uDye, coord - vec2(uTexelSize.x, 0.0)).rgb;
  neighborhood += texture2D(uDye, coord + vec2(0.0, uTexelSize.y)).rgb;
  neighborhood += texture2D(uDye, coord - vec2(0.0, uTexelSize.y)).rgb;
  neighborhood *= 0.25;

  color = mix(color, neighborhood, 0.002);
  color *= pow(0.025, uDt / 30.0);
  float speed = length(velocity);
  float slowness = 1.0 - smoothstep(0.004, 0.055, speed);
  float stillAge = texture2D(uStillness, vUv).r * 45.0;
  float stillFade = smoothstep(30.0, 45.0, stillAge);
  float idleFadeStart = mix(38.0, 30.0, slowness);
  float idleProgress = clamp((uIdleTime - idleFadeStart) / max(45.0 - idleFadeStart, 0.001), 0.0, 1.0);
  float idleFade = smoothstep(0.0, 1.0, idleProgress);
  float finalClear = smoothstep(44.7, 45.0, uIdleTime);
  float stagnantFade = max(stillFade, max(idleFade, finalClear));
  color *= pow(0.001, stagnantFade * uDt / 15.0);
  color *= 1.0 - finalClear;

  gl_FragColor = vec4(color, 1.0);
}
`

const UPDATE_STILLNESS_SHADER = `
precision PRECISION float;

varying vec2 vUv;

uniform sampler2D uStillness;
uniform sampler2D uDye;
uniform sampler2D uVelocity;
uniform float uDt;

vec2 decodeVelocity(vec4 value) {
  return value.xy * 2.0 - 1.0;
}

void main() {
  vec2 velocity = decodeVelocity(texture2D(uVelocity, vUv));
  vec2 coord = clamp(vUv - velocity * uDt * 0.48, 0.001, 0.999);
  vec3 color = texture2D(uDye, coord).rgb;
  float colorAmount = max(max(color.r, color.g), color.b);
  float hasColor = smoothstep(0.012, 0.04, colorAmount);
  float moving = smoothstep(0.014, 0.055, length(velocity));
  float stillAge = texture2D(uStillness, coord).r * 45.0;

  stillAge += uDt * hasColor * (1.0 - moving);
  stillAge -= uDt * (moving * 4.0 + (1.0 - hasColor) * 8.0);
  stillAge = clamp(stillAge, 0.0, 45.0);

  gl_FragColor = vec4(stillAge / 45.0, 0.0, 0.0, 1.0);
}
`

const SPLAT_DYE_SHADER = `
precision PRECISION float;

varying vec2 vUv;

uniform sampler2D uDye;
uniform vec2 uAspect;
uniform vec2 uPointer;
uniform vec2 uPointerPrevious;
uniform vec3 uDropColor;
uniform float uDropRadius;
uniform float uDropStrength;

float segmentDistance(vec2 point, vec2 start, vec2 end) {
  vec2 segment = (end - start) * uAspect;
  vec2 offset = (point - start) * uAspect;
  float h = clamp(dot(offset, segment) / max(dot(segment, segment), 0.00001), 0.0, 1.0);
  return length(offset - segment * h);
}

void main() {
  vec3 color = texture2D(uDye, vUv).rgb;
  vec2 centerOffset = (vUv - uPointer) * uAspect;
  float pathDistance = segmentDistance(vUv, uPointerPrevious, uPointer);
  float pathInk = exp(-(pathDistance * pathDistance) / max(uDropRadius * uDropRadius, 0.00001));
  float bead = exp(-dot(centerOffset, centerOffset) / max(uDropRadius * uDropRadius * 1.8, 0.00001));
  float ink = clamp(pathInk * 0.72 + bead * 0.32, 0.0, 1.0) * uDropStrength;
  float ringDistance = abs(length(centerOffset) - uDropRadius * 1.25);
  float rim = 1.0 - smoothstep(uDropRadius * 0.12, uDropRadius * 0.34, ringDistance);

  color = mix(color, uDropColor, ink);
  color = mix(color, vec3(0.006, 0.012, 0.018), rim * ink * 0.12);

  gl_FragColor = vec4(color, 1.0);
}
`

const SPLAT_VELOCITY_SHADER = `
precision PRECISION float;

varying vec2 vUv;

uniform sampler2D uVelocity;
uniform vec2 uAspect;
uniform vec2 uPoint;
uniform vec2 uJetDirection;
uniform float uJetLength;
uniform float uRadius;
uniform float uForce;
uniform float uJetForce;
uniform float uSwirl;

vec2 decodeVelocity(vec4 value) {
  return value.xy * 2.0 - 1.0;
}

vec2 encodeVelocity(vec2 velocity) {
  return clamp(velocity * 0.5 + 0.5, 0.0, 1.0);
}

void main() {
  vec2 velocity = decodeVelocity(texture2D(uVelocity, vUv));
  vec2 offset = (vUv - uPoint) * uAspect;
  float dist = length(offset) + 0.0001;
  vec2 direction = offset / dist;
  vec2 jetDirection = normalize(uJetDirection + vec2(0.0001));
  vec2 tangent = vec2(-direction.y, direction.x);
  float influence = exp(-(dist * dist) / max(uRadius * uRadius, 0.00001));
  float centerLift = 1.0 - smoothstep(0.0, uRadius * 0.42, dist);
  float alongJet = dot(offset, jetDirection);
  vec2 closestOnJet = jetDirection * clamp(alongJet, 0.0, uJetLength);
  float jetDistance = length(offset - closestOnJet);
  float jetFront = smoothstep(-uRadius * 0.4, uRadius * 0.3, alongJet);
  float jetTail = 1.0 - smoothstep(uJetLength, uJetLength + uRadius * 1.9, alongJet);
  float jetInfluence = exp(-(jetDistance * jetDistance) / max(uRadius * uRadius, 0.00001)) * jetFront * jetTail;

  velocity += direction * influence * uForce;
  velocity += jetDirection * jetInfluence * uJetForce;
  velocity += tangent * influence * uSwirl;
  velocity += direction * centerLift * uForce * 0.35;
  velocity = clamp(velocity, vec2(-0.78), vec2(0.78));

  gl_FragColor = vec4(encodeVelocity(velocity), 0.0, 1.0);
}
`

const DISPLAY_SHADER = `
precision PRECISION float;

varying vec2 vUv;

uniform sampler2D uDye;
uniform sampler2D uVelocity;
uniform vec2 uResolution;
uniform float uTime;
uniform float uSeed;

float hash(vec2 p) {
  p = fract(p * vec2(234.21, 117.77));
  p += dot(p, p + 19.19 + uSeed);
  return fract(p.x * p.y);
}

vec2 decodeVelocity(vec4 value) {
  return value.xy * 2.0 - 1.0;
}

void main() {
  vec2 texel = 1.0 / uResolution;
  vec2 velocity = decodeVelocity(texture2D(uVelocity, vUv));
  vec2 shimmer = velocity * 0.006 + vec2(sin(uTime * 0.21 + vUv.y * 9.0), cos(uTime * 0.17 + vUv.x * 7.0)) * 0.00025;
  vec3 color = texture2D(uDye, clamp(vUv + shimmer, 0.001, 0.999)).rgb;

  vec3 left = texture2D(uDye, vUv - vec2(texel.x * 1.8, 0.0)).rgb;
  vec3 right = texture2D(uDye, vUv + vec2(texel.x * 1.8, 0.0)).rgb;
  vec3 bottom = texture2D(uDye, vUv - vec2(0.0, texel.y * 1.8)).rgb;
  vec3 top = texture2D(uDye, vUv + vec2(0.0, texel.y * 1.8)).rgb;
  float edge = length(right - left) + length(top - bottom);

  color += edge * vec3(0.026, 0.03, 0.034);

  float vignette = smoothstep(0.96, 0.18, length(vUv - 0.5));
  color *= mix(0.62, 1.02, vignette);
  color = pow(max(color, vec3(0.0)), vec3(0.92));

  gl_FragColor = vec4(color, 1.0);
}
`

function getPrecision(gl) {
  const highp = gl.getShaderPrecisionFormat(gl.FRAGMENT_SHADER, gl.HIGH_FLOAT)
  return highp && highp.precision > 0 ? 'highp' : 'mediump'
}

function createShader(gl, type, source) {
  const shader = gl.createShader(type)
  gl.shaderSource(shader, source)
  gl.compileShader(shader)

  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const message = gl.getShaderInfoLog(shader) || 'Unknown shader compile error'
    gl.deleteShader(shader)
    throw new Error(message)
  }

  return shader
}

function createProgram(gl, vertexSource, fragmentSource) {
  const program = gl.createProgram()
  const vertex = createShader(gl, gl.VERTEX_SHADER, vertexSource)
  const fragment = createShader(gl, gl.FRAGMENT_SHADER, fragmentSource)

  gl.attachShader(program, vertex)
  gl.attachShader(program, fragment)
  gl.linkProgram(program)
  gl.deleteShader(vertex)
  gl.deleteShader(fragment)

  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const message = gl.getProgramInfoLog(program) || 'Unknown shader link error'
    gl.deleteProgram(program)
    throw new Error(message)
  }

  return program
}

function getUniformLocations(gl, program, names) {
  return names.reduce((locations, name) => {
    locations[name] = gl.getUniformLocation(program, name)
    return locations
  }, {})
}

function setPrecision(source, precision) {
  return source.replace(/PRECISION/g, precision)
}

function createTarget(gl, width, height) {
  const texture = gl.createTexture()
  gl.bindTexture(gl.TEXTURE_2D, texture)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null)

  const fbo = gl.createFramebuffer()
  gl.bindFramebuffer(gl.FRAMEBUFFER, fbo)
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0)

  if (gl.checkFramebufferStatus(gl.FRAMEBUFFER) !== gl.FRAMEBUFFER_COMPLETE) {
    throw new Error('Could not create framebuffer for suminagashi simulation.')
  }

  return { texture, fbo, width, height }
}

function createDoubleTarget(gl, width, height) {
  return {
    read: createTarget(gl, width, height),
    write: createTarget(gl, width, height),
    width,
    height,
    texelSize: [1 / width, 1 / height],
    swap() {
      const temp = this.read
      this.read = this.write
      this.write = temp
    },
  }
}

export class MarblingSimulation {
  constructor(canvas) {
    this.canvas = canvas
    this.gl =
      canvas.getContext('webgl2', {
        alpha: false,
        antialias: false,
        depth: false,
        stencil: false,
        preserveDrawingBuffer: false,
      }) ||
      canvas.getContext('webgl', {
        alpha: false,
        antialias: false,
        depth: false,
        stencil: false,
        preserveDrawingBuffer: false,
      }) ||
      canvas.getContext('experimental-webgl')

    if (!this.gl) {
      throw new Error('WebGL is required for the suminagashi background.')
    }

    this.time = 0
    this.seed = Math.random() * 1000
    this.pointer = { x: 0.5, y: 0.5 }
    this.pointerPrevious = { x: 0.5, y: 0.5 }
    this.pointerVelocity = { x: 0, y: 0 }
    this.pointerActive = 0
    this.dropActive = 0
    this.motionEnergy = 0
    this.queuedDrops = []
    this.hasPointer = false
    this.lastHumanInputTime = -Infinity
    this.ghost = {
      active: false,
      hasPosition: false,
      x: 0.5,
      y: 0.5,
      startX: 0.5,
      startY: 0.5,
      targetX: 0.5,
      targetY: 0.5,
      curveAmp: 0.04,
      curveSign: 1,
      wiggleAmp: 0.01,
      wiggleFreq: 3,
      wigglePhase: 0,
      startTime: 0,
      endTime: 0,
      nextMoveTime: 1.4,
    }
    this.dye = null
    this.velocity = null
    this.stillness = null

    this.gl.disable(this.gl.DEPTH_TEST)
    this.gl.disable(this.gl.BLEND)

    this.initPrograms()
    this.initGeometry()
    this.resize(true)
  }

  initPrograms() {
    const gl = this.gl
    const precision = getPrecision(gl)

    this.programs = {
      initDye: createProgram(gl, VERTEX_SHADER, setPrecision(INIT_DYE_SHADER, precision)),
      initVelocity: createProgram(gl, VERTEX_SHADER, setPrecision(INIT_VELOCITY_SHADER, precision)),
      updateVelocity: createProgram(gl, VERTEX_SHADER, setPrecision(UPDATE_VELOCITY_SHADER, precision)),
      updateStillness: createProgram(gl, VERTEX_SHADER, setPrecision(UPDATE_STILLNESS_SHADER, precision)),
      advectDye: createProgram(gl, VERTEX_SHADER, setPrecision(ADVECT_DYE_SHADER, precision)),
      splatDye: createProgram(gl, VERTEX_SHADER, setPrecision(SPLAT_DYE_SHADER, precision)),
      splatVelocity: createProgram(gl, VERTEX_SHADER, setPrecision(SPLAT_VELOCITY_SHADER, precision)),
      display: createProgram(gl, VERTEX_SHADER, setPrecision(DISPLAY_SHADER, precision)),
    }

    this.uniforms = {
      initDye: getUniformLocations(gl, this.programs.initDye, ['uResolution', 'uSeed']),
      initVelocity: getUniformLocations(gl, this.programs.initVelocity, ['uSeed']),
      updateVelocity: getUniformLocations(gl, this.programs.updateVelocity, [
        'uVelocity',
        'uTexelSize',
        'uAspect',
        'uPointer',
        'uPointerPrevious',
        'uPointerVelocity',
        'uPointerActive',
        'uMotionEnergy',
        'uTime',
        'uDt',
        'uSeed',
      ]),
      updateStillness: getUniformLocations(gl, this.programs.updateStillness, [
        'uStillness',
        'uDye',
        'uVelocity',
        'uDt',
      ]),
      advectDye: getUniformLocations(gl, this.programs.advectDye, [
        'uDye',
        'uVelocity',
        'uStillness',
        'uTexelSize',
        'uDt',
        'uIdleTime',
      ]),
      splatDye: getUniformLocations(gl, this.programs.splatDye, [
        'uDye',
        'uAspect',
        'uPointer',
        'uPointerPrevious',
        'uDropColor',
        'uDropRadius',
        'uDropStrength',
      ]),
      splatVelocity: getUniformLocations(gl, this.programs.splatVelocity, [
        'uVelocity',
        'uAspect',
        'uPoint',
        'uJetDirection',
        'uJetLength',
        'uRadius',
        'uForce',
        'uJetForce',
        'uSwirl',
      ]),
      display: getUniformLocations(gl, this.programs.display, ['uDye', 'uVelocity', 'uResolution', 'uTime', 'uSeed']),
    }
  }

  initGeometry() {
    const gl = this.gl
    this.quadBuffer = gl.createBuffer()

    gl.bindBuffer(gl.ARRAY_BUFFER, this.quadBuffer)
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([
        -1, -1,
        1, -1,
        -1, 1,
        -1, 1,
        1, -1,
        1, 1,
      ]),
      gl.STATIC_DRAW
    )
  }

  createBuffers() {
    const gl = this.gl
    const aspect = window.innerWidth / Math.max(window.innerHeight, 1)
    const dpr = Math.min(window.devicePixelRatio || 1, 1.35)
    const dyeWidth = Math.min(1120, Math.max(520, Math.floor(window.innerWidth * dpr * 0.78)))
    const dyeHeight = Math.max(320, Math.floor(dyeWidth / Math.max(aspect, 0.45)))
    const velocityWidth = Math.max(192, Math.floor(dyeWidth * 0.5))
    const velocityHeight = Math.max(128, Math.floor(dyeHeight * 0.5))

    this.deleteBuffers()

    this.dye = createDoubleTarget(gl, dyeWidth, dyeHeight)
    this.velocity = createDoubleTarget(gl, velocityWidth, velocityHeight)
    this.stillness = createDoubleTarget(gl, dyeWidth, dyeHeight)

    this.blit(this.dye.read, this.programs.initDye, this.uniforms.initDye, {
      uResolution: [dyeWidth, dyeHeight],
      uSeed: this.seed,
    })
    this.blit(this.stillness.read, this.programs.initDye, this.uniforms.initDye, {
      uResolution: [dyeWidth, dyeHeight],
      uSeed: this.seed,
    })
    this.blit(this.velocity.read, this.programs.initVelocity, this.uniforms.initVelocity, {
      uSeed: this.seed,
    })
  }

  resize(force = false) {
    const dpr = Math.min(window.devicePixelRatio || 1, 1.35)
    const width = Math.max(1, Math.floor(window.innerWidth * dpr))
    const height = Math.max(1, Math.floor(window.innerHeight * dpr))

    if (this.canvas.width !== width || this.canvas.height !== height) {
      this.canvas.width = width
      this.canvas.height = height
      this.canvas.style.width = `${window.innerWidth}px`
      this.canvas.style.height = `${window.innerHeight}px`
      force = true
    }

    if (
      force ||
      !this.dye ||
      Math.abs(this.dye.width / this.dye.height - width / height) > 0.04
    ) {
      this.createBuffers()
    }
  }

  bindQuad(program) {
    const gl = this.gl
    const location = gl.getAttribLocation(program, 'aPosition')
    gl.bindBuffer(gl.ARRAY_BUFFER, this.quadBuffer)
    gl.enableVertexAttribArray(location)
    gl.vertexAttribPointer(location, 2, gl.FLOAT, false, 0, 0)
  }

  setUniform(location, value, textureUnit) {
    const gl = this.gl

    if (location === null) {
      return textureUnit
    }

    if (value?.texture) {
      gl.activeTexture(gl.TEXTURE0 + textureUnit)
      gl.bindTexture(gl.TEXTURE_2D, value.texture)
      gl.uniform1i(location, textureUnit)
      return textureUnit + 1
    }

    if (typeof value === 'number') {
      gl.uniform1f(location, value)
    } else if (value?.length === 2) {
      gl.uniform2f(location, value[0], value[1])
    } else if (value?.length === 3) {
      gl.uniform3f(location, value[0], value[1], value[2])
    }

    return textureUnit
  }

  blit(target, program, locations, uniforms = {}) {
    const gl = this.gl

    gl.bindFramebuffer(gl.FRAMEBUFFER, target ? target.fbo : null)
    gl.viewport(0, 0, target ? target.width : this.canvas.width, target ? target.height : this.canvas.height)
    gl.useProgram(program)
    this.bindQuad(program)

    let textureUnit = 0
    Object.entries(uniforms).forEach(([name, value]) => {
      textureUnit = this.setUniform(locations[name], value, textureUnit)
    })

    gl.drawArrays(gl.TRIANGLES, 0, 6)
  }

  getCanvasPoint(x, y) {
    return {
      x: x / Math.max(window.innerWidth, 1),
      y: 1 - y / Math.max(window.innerHeight, 1),
    }
  }

  applyPointerMotion(point, options = {}) {
    const {
      hasPrevious = this.hasPointer,
      previous = this.pointer,
      pointerActive = 1,
      dropScale = 95,
      energyScale = 42,
      claimPointer = false,
    } = options
    const previousPoint = hasPrevious ? previous : point
    const dx = point.x - previousPoint.x
    const dy = point.y - previousPoint.y
    const speed = Math.hypot(dx, dy)

    this.pointerPrevious = { ...previousPoint }
    this.pointer = { ...point }
    this.pointerVelocity = {
      x: this.pointerVelocity.x * 0.48 + dx * 0.52,
      y: this.pointerVelocity.y * 0.48 + dy * 0.52,
    }
    this.pointerActive = pointerActive
    this.dropActive = Math.min(1, speed * dropScale)
    this.motionEnergy = Math.min(1, this.motionEnergy + speed * energyScale)

    if (claimPointer) {
      this.hasPointer = true
    }

    return { dx, dy, speed }
  }

  setMouse(x, y) {
    const point = this.getCanvasPoint(x, y)
    this.lastHumanInputTime = this.time
    this.ghost.active = false
    this.applyPointerMotion(point, { claimPointer: true })
  }

  addColorDrop(x, y) {
    const point = this.getCanvasPoint(x, y)
    const color = this.getDropColor()

    this.queuedDrops.push({
      x: point.x,
      y: point.y,
      color,
      radius: 0.054,
      strength: 0.9,
      forceRadius: 0.102,
      force: 0.064,
      jetForce: 0,
      swirl: 0.018,
      puffs: [],
    })

    if (this.queuedDrops.length > 8) {
      this.queuedDrops.shift()
    }

    this.lastHumanInputTime = this.time
    this.ghost.active = false
    this.motionEnergy = Math.min(1, this.motionEnergy + 0.44)
  }

  releasePointer() {
    const wasActive = this.hasPointer || this.pointerActive > 0.01 || this.dropActive > 0.01

    this.pointerActive = 0
    this.dropActive = 0
    this.hasPointer = false

    if (wasActive) {
      this.lastHumanInputTime = this.time
    }
  }

  getDropColor() {
    const colors = [
      [0.0, 0.92, 0.95],
      [0.36, 0.7, 1.0],
      [1.0, 0.48, 0.08],
      [0.9, 0.06, 0.025],
      [1.0, 0.18, 0.58],
      [0.58, 0.18, 1.0],
    ]
    const phase = (this.time * 0.12) % colors.length
    const index = Math.floor(phase)
    const nextIndex = (index + 1) % colors.length
    const t = phase - index
    const eased = t * t * (3 - 2 * t)

    return colors[index].map((value, channel) => (
      value + (colors[nextIndex][channel] - value) * eased
    ))
  }

  scheduleGhostMove(delay = 0) {
    const margin = 0.08
    const maxStep = 0.3

    this.ghost.active = false
    this.ghost.startX = this.ghost.x
    this.ghost.startY = this.ghost.y
    this.ghost.targetX = Math.max(
      margin,
      Math.min(1 - margin, this.ghost.x + (Math.random() * 2 - 1) * maxStep),
    )
    this.ghost.targetY = Math.max(
      margin,
      Math.min(1 - margin, this.ghost.y + (Math.random() * 2 - 1) * maxStep),
    )
    const dx = this.ghost.targetX - this.ghost.startX
    const dy = this.ghost.targetY - this.ghost.startY
    const distance = Math.hypot(dx, dy)

    this.ghost.curveAmp = Math.min(0.18, 0.055 + distance * (0.34 + Math.random() * 0.22))
    this.ghost.curveSign = Math.random() < 0.5 ? -1 : 1
    this.ghost.wiggleAmp = 0.016 + Math.random() * 0.026
    this.ghost.wiggleFreq = 3.0 + Math.floor(Math.random() * 4)
    this.ghost.wigglePhase = Math.random() * Math.PI * 2
    this.ghost.startTime = this.time + delay
    this.ghost.endTime = this.ghost.startTime + 1.25 + Math.random() * 1.6
    this.ghost.nextMoveTime = this.ghost.startTime
  }

  updateGhostCursor() {
    const idleTime = this.time - this.lastHumanInputTime

    if (this.hasPointer || idleTime < 1.0) {
      return
    }

    if (!this.ghost.hasPosition) {
      this.ghost.x = 0.12 + Math.random() * 0.76
      this.ghost.y = 0.12 + Math.random() * 0.76
      this.ghost.hasPosition = true
      this.scheduleGhostMove(0.12 + Math.random() * 0.32)
      return
    }

    if (!this.ghost.active && this.time >= this.ghost.nextMoveTime) {
      this.ghost.active = true
      this.ghost.startX = this.ghost.x
      this.ghost.startY = this.ghost.y
    }

    if (!this.ghost.active || this.time < this.ghost.startTime) {
      return
    }

    const duration = Math.max(0.001, this.ghost.endTime - this.ghost.startTime)
    const t = Math.min(1, (this.time - this.ghost.startTime) / duration)
    const eased = t * t * (3 - 2 * t)
    const dx = this.ghost.targetX - this.ghost.startX
    const dy = this.ghost.targetY - this.ghost.startY
    const distance = Math.hypot(dx, dy) || 1
    const normalX = -dy / distance
    const normalY = dx / distance
    const edgeFade = Math.sin(Math.PI * t)
    const sCurve = Math.sin(Math.PI * 2 * eased) * this.ghost.curveAmp * this.ghost.curveSign
    const squiggle = (
      Math.sin(Math.PI * 2 * this.ghost.wiggleFreq * eased + this.ghost.wigglePhase) +
      Math.sin(Math.PI * (this.ghost.wiggleFreq + 1.5) * eased - this.ghost.wigglePhase * 0.7) * 0.45
    )
      * this.ghost.wiggleAmp
      * edgeFade
    const curveOffset = sCurve + squiggle
    const point = {
      x: Math.max(0.002, Math.min(0.998, this.ghost.startX + dx * eased + normalX * curveOffset)),
      y: Math.max(0.002, Math.min(0.998, this.ghost.startY + dy * eased + normalY * curveOffset)),
    }
    const previous = { x: this.ghost.x, y: this.ghost.y }

    this.applyPointerMotion(point, {
      hasPrevious: true,
      previous,
      pointerActive: 0.66,
      dropScale: 88,
      energyScale: 22,
      claimPointer: false,
    })

    this.ghost.x = point.x
    this.ghost.y = point.y

    if (t >= 1) {
      this.ghost.active = false
      this.ghost.nextMoveTime = this.time + 0.08 + Math.random() * 0.36
      this.scheduleGhostMove(this.ghost.nextMoveTime - this.time)
    }
  }

  stepVelocity(dt) {
    const aspect = this.canvas.width / Math.max(this.canvas.height, 1)

    this.blit(this.velocity.write, this.programs.updateVelocity, this.uniforms.updateVelocity, {
      uVelocity: this.velocity.read,
      uTexelSize: this.velocity.texelSize,
      uAspect: [aspect, 1],
      uPointer: [this.pointer.x, this.pointer.y],
      uPointerPrevious: [this.pointerPrevious.x, this.pointerPrevious.y],
      uPointerVelocity: [this.pointerVelocity.x, this.pointerVelocity.y],
      uPointerActive: this.pointerActive,
      uMotionEnergy: this.motionEnergy,
      uTime: this.time,
      uDt: dt,
      uSeed: this.seed,
    })
    this.velocity.swap()

    this.queuedDrops.forEach((drop) => {
      this.blit(this.velocity.write, this.programs.splatVelocity, this.uniforms.splatVelocity, {
        uVelocity: this.velocity.read,
        uAspect: [aspect, 1],
        uPoint: [drop.x, drop.y],
        uJetDirection: [0, 0],
        uJetLength: 0,
        uRadius: drop.forceRadius,
        uForce: drop.force,
        uJetForce: drop.jetForce,
        uSwirl: drop.swirl,
      })
      this.velocity.swap()

      drop.puffs.forEach((puff) => {
        this.blit(this.velocity.write, this.programs.splatVelocity, this.uniforms.splatVelocity, {
          uVelocity: this.velocity.read,
          uAspect: [aspect, 1],
          uPoint: [drop.x, drop.y],
          uJetDirection: puff.direction,
          uJetLength: puff.jetLength,
          uRadius: puff.forceRadius,
          uForce: 0.012,
          uJetForce: puff.force,
          uSwirl: 0.006,
        })
        this.velocity.swap()
      })
    })
  }

  stepStillness(dt) {
    this.blit(this.stillness.write, this.programs.updateStillness, this.uniforms.updateStillness, {
      uStillness: this.stillness.read,
      uDye: this.dye.read,
      uVelocity: this.velocity.read,
      uDt: dt,
    })
    this.stillness.swap()
  }

  stepDye(dt) {
    const idleTime = this.hasPointer ? Math.max(0, this.time - this.lastHumanInputTime) : 0

    this.blit(this.dye.write, this.programs.advectDye, this.uniforms.advectDye, {
      uDye: this.dye.read,
      uVelocity: this.velocity.read,
      uStillness: this.stillness.read,
      uTexelSize: this.dye.texelSize,
      uDt: dt,
      uIdleTime: idleTime,
    })
    this.dye.swap()

    if (this.dropActive > 0.01) {
      const aspect = this.canvas.width / Math.max(this.canvas.height, 1)
      const pointerSpeed = Math.min(1, Math.hypot(this.pointerVelocity.x * aspect, this.pointerVelocity.y) * 35)

      this.blit(this.dye.write, this.programs.splatDye, this.uniforms.splatDye, {
        uDye: this.dye.read,
        uAspect: [aspect, 1],
        uPointer: [this.pointer.x, this.pointer.y],
        uPointerPrevious: [this.pointerPrevious.x, this.pointerPrevious.y],
        uDropColor: this.getDropColor(),
        uDropRadius: 0.004 + pointerSpeed * 0.0055,
        uDropStrength: (0.32 + pointerSpeed * 0.46) * this.dropActive,
      })
      this.dye.swap()
    }

    this.queuedDrops.forEach((drop) => {
      this.blit(this.dye.write, this.programs.splatDye, this.uniforms.splatDye, {
        uDye: this.dye.read,
        uAspect: [this.canvas.width / Math.max(this.canvas.height, 1), 1],
        uPointer: [drop.x, drop.y],
        uPointerPrevious: [drop.x, drop.y],
        uDropColor: drop.color,
        uDropRadius: drop.radius,
        uDropStrength: drop.strength,
      })
      this.dye.swap()

      drop.puffs.forEach((puff) => {
        this.blit(this.dye.write, this.programs.splatDye, this.uniforms.splatDye, {
          uDye: this.dye.read,
          uAspect: [this.canvas.width / Math.max(this.canvas.height, 1), 1],
          uPointer: [puff.x, puff.y],
          uPointerPrevious: [drop.x, drop.y],
          uDropColor: drop.color,
          uDropRadius: puff.radius,
          uDropStrength: puff.strength,
        })
        this.dye.swap()
      })
    })
    this.queuedDrops.length = 0
  }

  render() {
    this.blit(null, this.programs.display, this.uniforms.display, {
      uDye: this.dye.read,
      uVelocity: this.velocity.read,
      uResolution: [this.canvas.width, this.canvas.height],
      uTime: this.time,
      uSeed: this.seed,
    })
  }

  update(dt) {
    this.time += dt
    this.resize()

    const stableDt = Math.min(dt, 1 / 30)
    this.updateGhostCursor()
    this.stepVelocity(stableDt)
    this.stepStillness(stableDt)
    this.stepDye(stableDt)
    this.render()

    this.pointerVelocity.x *= 0.92
    this.pointerVelocity.y *= 0.92
    this.pointerActive *= 0.94
    this.dropActive *= 0.72
    this.motionEnergy *= this.pointerActive > 0.01 ? 0.982 : 0.94
    this.pointerPrevious = { ...this.pointer }
  }

  deleteBuffers() {
    const gl = this.gl
    ;[this.dye, this.velocity, this.stillness].forEach((buffer) => {
      if (!buffer) return

      ;[buffer.read, buffer.write].forEach((target) => {
        gl.deleteTexture(target.texture)
        gl.deleteFramebuffer(target.fbo)
      })
    })
  }

  destroy() {
    const gl = this.gl
    this.deleteBuffers()

    if (this.quadBuffer) {
      gl.deleteBuffer(this.quadBuffer)
    }

    Object.values(this.programs).forEach((program) => {
      gl.deleteProgram(program)
    })
  }
}
